// src/pages/Achievements/Achievements.js
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { db } from "../../firebase";
import {
  doc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import ProfileCard from "./components/ProfileCard";
import AchievementCard from "./components/AchievementCard";
import "./css/Achievements.css";

// ReactionButton kept IN THIS FILE (Achievements.js)
function ReactionButton({ emoji, label, count = 0, onClick, isActive = false }) {
  const hasCount = Number(count) > 0;
  return (
    <button
      type="button"
      className={[
        "reaction-btn",
        isActive ? "active" : "",
        hasCount ? "has-count" : "",
      ].join(" ").trim()}
      onClick={onClick}
      aria-pressed={!!isActive}
      aria-label={`${label}. ${hasCount ? `${count} reactions` : "0 reactions"}`}
      title={label}
    >
      <span className="emoji" aria-hidden="true">{emoji}</span>
      <span className="count" aria-hidden="true" data-count={count}>
        {count}
      </span>
    </button>
  );
}

/**
 * Full reaction set used by UI
 */
const REACTIONS = [
  { id: "like",       emoji: "👍", label: "Like" },
  { id: "love",       emoji: "❤️", label: "Love" },
  { id: "fire",       emoji: "🔥", label: "Hot" },
  { id: "applause",   emoji: "👏", label: "Applause" },
  { id: "celebrate",  emoji: "🎉", label: "Celebrate" },
  { id: "funny",      emoji: "😂", label: "Funny" },
  { id: "respect",    emoji: "🤝", label: "Respect" },
  { id: "100",        emoji: "💯", label: "100" },
  { id: "helpful",    emoji: "💡", label: "Helpful" },
  { id: "strategy",   emoji: "🎯", label: "Strategy" },
  { id: "epic",       emoji: "😎", label: "Epic Moment" },
  { id: "insightful", emoji: "🧠", label: "Insightful" },
  { id: "curious",    emoji: "❓", label: "Curious" },
  { id: "agree",      emoji: "✅", label: "Agree" },
  { id: "disagree",   emoji: "❌", label: "Disagree" },
  { id: "support",    emoji: "🤗", label: "Support" },
  { id: "bookmark",   emoji: "🔖", label: "Save" },
  { id: "bug",        emoji: "🐞", label: "Issue" },
  { id: "cool",       emoji: "🆒", label: "Cool" },
  { id: "wow",        emoji: "😮", label: "Wow" },
  { id: "music",      emoji: "🎶", label: "Music" },
];

// helper map for normalization
const REACTION_BY_ID = REACTIONS.reduce((m, r) => ((m[r.id] = r), m), {});
const REACTION_BY_EMOJI = REACTIONS.reduce((m, r) => ((m[r.emoji] = r), m), {});

export default function AchievementsPage({ user: propUser = null, setShowAuthModal: propSetShowAuthModal = null }) {
  // prefer outlet context, but fall back to props (for embedded mode)
  const outlet = useOutletContext?.() ?? {};
  const currentUser = outlet.user ?? propUser ?? null;
  const setShowAuthModal = outlet.setShowAuthModal ?? propSetShowAuthModal ?? (() => {});

  // optional app toast hook (wire your toast system here)
  // const { toast } = useToast() || {};
  const [myProfile, setMyProfile] = useState(null);
  const [myAchievements, setMyAchievements] = useState([]);
  const [loadingMy, setLoadingMy] = useState(true);

  const [leaders, setLeaders] = useState([]);
  const [loadingLeaders, setLoadingLeaders] = useState(true);

  // optimistic reactions map to show immediate UI changes:
  // key = `${parentCollection}/${parentId}`, value = { reactorUid, type }
  const [optimisticReactions, setOptimisticReactions] = useState({});

  // track whether component is mounted to avoid state updates after unmount
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // track inflight writes to prevent rapid double-submits / race conditions
  const inflightRef = useRef({}); // keys same as optimistic map; value = true

  // announce optimistic reaction changes for screen readers
  const ariaLiveRef = useRef(null);

  // Load logged-in user's public profile + achievements
  useEffect(() => {
    if (!currentUser) {
      setMyProfile(null);
      setMyAchievements([]);
      setLoadingMy(false);
      return;
    }
    let mounted = true;
    const ppRef = doc(db, "public_profiles", currentUser.uid);

    // realtime profile snapshot
    const unsubProfile = onSnapshot(
      ppRef,
      (snap) => {
        if (!mounted) return;
        setMyProfile(snap.exists() ? { uid: snap.id, ...snap.data() } : null);
        setLoadingMy(false);
      },
      (err) => {
        console.error("public_profiles listener error:", err);
        setLoadingMy(false);
      }
    );

    // latest unlocked achievements (limit 12)
    (async () => {
      try {
        const q = query(
          collection(db, "achievements"),
          where("uid", "==", currentUser.uid),
          where("unlocked", "==", true),
          orderBy("unlockedAt", "desc"),
          limit(12)
        );
        const snap = await getDocs(q);
        if (!mounted) return;
        setMyAchievements(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Failed to load my achievements", err);
      }
    })();

    return () => {
      mounted = false;
      try {
        unsubProfile && unsubProfile();
      } catch (e) {}
    };
  }, [currentUser]);

  // Load top leaderboard (public_profiles sorted by xp)
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingLeaders(true);
      try {
        const q = query(collection(db, "public_profiles"), orderBy("xp", "desc"), limit(12));
        const snap = await getDocs(q);
        if (!mounted) return;
        const list = snap.docs
          .map((d) => ({ uid: d.id, ...d.data() }))
          .filter((p) => p.uid !== currentUser?.uid);
        setLeaders(list);
      } catch (err) {
        console.error("Failed to load leaders", err);
      } finally {
        if (mounted) setLoadingLeaders(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [currentUser]);

  /**
   * toggleReaction (now optimistic + protected)
   * Writes per-reactor doc to: <parentCollection>/<parentId>/reactions/<reactorUid>
   * Accepts either an id (like "like") or an emoji (like "👍") in `emoji`.
   * existing may be id or emoji. If reactor clicks same reaction again, delete doc (unreact).
   *
   * Improvements:
   * - prevents duplicate inflight writes
   * - announces optimistic changes to screen readers
   * - rolls back optimistic state on error
   */
  const toggleReaction = useCallback(async ({ parentCollection, parentId, reactorUid, emoji, existing }) => {
    if (!reactorUid) {
      setShowAuthModal(true);
      return;
    }

    // Normalize incoming to canonical id
    let reactionId = emoji;
    if (!REACTION_BY_ID[reactionId]) {
      const byEmoji = REACTION_BY_EMOJI[emoji];
      if (byEmoji) reactionId = byEmoji.id;
    }
    if (!reactionId) {
      console.warn("Unknown reaction:", emoji);
      return;
    }

    // Normalize existing to id
    let existingId = existing;
    if (existing && !REACTION_BY_ID[existingId]) {
      const byEmoji = REACTION_BY_EMOJI[existing];
      if (byEmoji) existingId = byEmoji.id;
    }

    const isSame = existingId === reactionId;
    const key = `${parentCollection}/${parentId}`;

    // Prevent concurrent writes for the same key
    if (inflightRef.current[key]) {
      // optional: provide feedback to user (toast) — left commented for integration
      // toast?.info?.("Still updating that reaction…");
      return;
    }

    // Optimistically update UI: set chosen reaction or clear if unreact
    setOptimisticReactions((prev) => {
      const next = { ...prev };
      if (isSame) {
        delete next[key];
      } else {
        next[key] = { reactorUid, type: reactionId };
      }
      return next;
    });

    // announce for SR users
    try {
      if (ariaLiveRef.current) {
        const label = REACTION_BY_ID[reactionId]?.label ?? reactionId;
        ariaLiveRef.current.textContent = isSame
          ? `Removed ${label}`
          : `Reacted ${label}`;
      }
    } catch (e) {}

    const refPath = doc(db, `${parentCollection}/${parentId}/reactions/${reactorUid}`);

    inflightRef.current[key] = true;
    try {
      if (isSame) {
        await deleteDoc(refPath);
        console.debug("[toggleReaction] deleted (optimistic) ->", key, reactionId);
      } else {
        await setDoc(refPath, { type: reactionId, createdAt: serverTimestamp() });
        console.debug("[toggleReaction] set (optimistic) ->", key, reactionId);
      }
    } catch (err) {
      // rollback optimistic change
      console.error("toggleReaction error, rolling back optimistic UI:", err);
      setOptimisticReactions((prev) => {
        const next = { ...prev };
        // rollback: revert to previous state by removing optimistic entry for this key
        delete next[key];
        return next;
      });
      // optional toast to user (uncomment and wire your toast system)
      // toast?.error?.("Couldn't update reaction. Please try again.");
    } finally {
      delete inflightRef.current[key];
    }
  }, [setShowAuthModal]);

  /**
   * helper wrapper generators so children get stable handlers
   * They accept (emoji, existing) and call toggleReaction with correct parent info
   *
   * Improvements:
   * - create and memoize handlers so child components don't get new functions each render
   * - use appropriate parent collection for profiles vs achievements
   */
  const onReactForMyProfile = useMemo(() => {
    if (!currentUser) return () => setShowAuthModal(true);
    return (emoji, existing) =>
      toggleReaction({
        parentCollection: "public_profiles", // profiles store reactions under their doc
        parentId: currentUser.uid,
        reactorUid: currentUser.uid,
        emoji,
        existing,
      });
  }, [currentUser, toggleReaction, setShowAuthModal]);

  // Achievement reaction handler generator (one per achievement id)
  const createOnReactForAchievement = useCallback((aId) => {
    return (emoji, existing) => toggleReaction({
      parentCollection: "achievements",
      parentId: aId,
      reactorUid: currentUser?.uid ?? null,
      emoji,
      existing,
    });
  }, [toggleReaction, currentUser]);

  // Leader/profile reaction handler generator
  const createOnReactForLeader = useCallback((leaderUid) => {
    return (emoji, existing) => toggleReaction({
      parentCollection: "public_profiles",
      parentId: leaderUid,
      reactorUid: currentUser?.uid ?? null,
      emoji,
      existing,
    });
  }, [toggleReaction, currentUser]);

  // small helper used by UI / children if needed: returns optimistic reaction for a given parent
  const getOptimisticFor = useCallback((parentCollection, parentId) => {
    return optimisticReactions[`${parentCollection}/${parentId}`] ?? null;
  }, [optimisticReactions]);

  return (
    <div className="ach-page">
      {/* ARIA live region for screen readers */}
      <div
        ref={ariaLiveRef}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        style={{ position: "absolute", left: -9999, top: "auto", width: 1, height: 1, overflow: "hidden" }}
      />

      {/* TOP ROW */}
      <section className="ach-top-row">
        <div className="ach-top-inner">
          {!currentUser ? (
            <div className="auth-prompt" role="region" aria-label="Sign in prompt">
              <p>Sign in to view your achievements.</p>
            </div>
          ) : (
            <div className="my-section">
              <div className="my-profile">
                {loadingMy ? (
                  <div className="skeleton profile-skeleton" aria-hidden="true" />
                ) : (
                  <>
                    <ProfileCard
                      profile={
                        myProfile ?? {
                          uid: currentUser.uid,
                          displayName: currentUser.displayName ?? "",
                          photoURL: currentUser.photoURL ?? "",
                          xp: 0,
                          level: "Rookie",
                          badges: [],
                        }
                      }
                      ReactionButtonComponent={ReactionButton}
                      currentUser={currentUser}
                      reactionCountsFromParent={myProfile?.reactionCounts}
                      onReact={(emoji, existing) => onReactForMyProfile(emoji, existing)}
                      reactions={REACTIONS}
                      optimisticReactions={optimisticReactions}
                      getOptimisticFor={getOptimisticFor}
                    />

                    {/* If the user has no unlocked achievements, show the helpful CTA below the profile icons */}
                    {!loadingMy && myAchievements.length === 0 && (
                      <div className="empty under-icons" role="status" aria-live="polite">
                        <p>Coming Soon!!</p>
                        {/* <p>You haven't unlocked achievements yet.</p>
                        <p className="muted-small">
                          Try playing missions, completing challenges, or linking external accounts to start unlocking achievements.
                        </p> */}
                      </div>
                    )}
                  </>
                )}
              </div>

              {loadingMy ? (
                <div className="my-achievements-list">
                  <div className="ach-grid">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="achievement-card skeleton" aria-hidden="true" />
                    ))}
                  </div>
                </div>
              ) : myAchievements.length > 0 ? (
                <div className="my-achievements-list">
                  <div className="ach-grid">
                    {myAchievements.map((a) => (
                      <AchievementCard
                        key={a.id}
                        achievement={a}
                        parentType="achievements"
                        ReactionButtonComponent={ReactionButton}
                        currentUser={currentUser}
                        reactionCountsFromParent={a.reactionCounts}
                        onReact={createOnReactForAchievement(a.id)}
                        reactions={REACTIONS}
                        optimisticReactions={optimisticReactions}
                        getOptimisticFor={getOptimisticFor}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </section>

      {/* BOTTOM ROW */}
      <section className="ach-bottom-row">
        <div className="ach-bottom-inner">
          <h2 className="section-title">Community Spotlight</h2>
          {loadingLeaders ? (
            <div>Loading community…</div>
          ) : leaders.length === 0 ? (
            <div className="empty">No public profiles found.</div>
          ) : (
            <div className="leaders-row-wrapper">
              <div className="leaders-grid" tabIndex="0" aria-label="Community spotlight list">
                {leaders.map((p) => (
                  <div key={p.uid} className="leader-tile">
                    <ProfileCard
                      profile={p}
                      currentUser={currentUser}
                      allowExpand
                      compact={true}
                      reactionCountsFromParent={p.reactionCounts}
                      ReactionButtonComponent={ReactionButton}
                      onReact={createOnReactForLeader(p.uid)}
                      reactions={REACTIONS}
                      optimisticReactions={optimisticReactions}
                      getOptimisticFor={getOptimisticFor}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
